// src/utils/subscriptionDates.ts
// Fuente ÚNICA de la cuenta de vencimientos de suscripción.
//
// Por qué existe: el aviso rojo del panel de admin («1 suscripción PRO vencida») y la lista
// a la que lleva su botón «Revisar» medían distinto. El aviso comparaba `endDate < ahora`
// con hora exacta, y la lista redondeaba a días completos. Resultado: el día del corte, una
// suscripción que vencía a las 10:34 contaba como vencida en el aviso pero no aparecía en la
// lista — contador en 1 y tabla vacía, sin forma de saber de quién se trataba (caso Cofico,
// 11-ago-2026). El criterio bueno es el de DÍAS: una suscripción no está vencida hasta que
// termina su día. Todo lo que cuente vencimientos debe usar estas funciones, no su propia copia.

export const parseDateSafe = (date: any): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
  if (date.toDate && typeof date.toDate === 'function') return date.toDate();
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/** Misma fecha a las 00:00 locales, para comparar días y no instantes. */
export const normalizeDate = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Días completos que faltan: 0 = vence hoy (aún vigente), negativo = ya venció. */
export const getDaysRemaining = (endDate: Date | null): number | null => {
  if (!endDate) return null;
  const validDate = parseDateSafe(endDate);
  if (!validDate) return null;
  const today = normalizeDate(new Date());
  const expiration = normalizeDate(validDate);
  return Math.floor((expiration.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

/** Vencida = su día ya pasó. «Vence hoy» todavía NO es vencida. */
export const isExpired = (endDate: Date | null): boolean => {
  if (!endDate) return false;
  const days = getDaysRemaining(endDate);
  return days !== null && days < 0;
};

/** Por vencer = vence hoy o dentro de los próximos `withinDays` días. */
export const isExpiringSoon = (endDate: Date | null, withinDays: number = 7): boolean => {
  if (!endDate) return false;
  const days = getDaysRemaining(endDate);
  return days !== null && days >= 0 && days <= withinDays;
};
